import { useState } from 'react'
import type { FullPokemonCard } from '@/types/pokemon'
import { PriceInput } from '../PriceInput'
import { STANDARD_CONDITIONS, type StandardCondition } from '@/types/inventory'

interface AddToInventoryModalProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: (condition: StandardCondition, priceWhenAdded: number | null) => void
  card: FullPokemonCard | null
}

export function AddToInventoryModal({ isOpen, onClose, onConfirm, card }: AddToInventoryModalProps) {
  const [condition, setCondition] = useState<StandardCondition>('NearMint')
  const [userPrice, setUserPrice] = useState<number | null>(null)
  const [priceError, setPriceError] = useState<string | null>(null)

  const defaultPrice = card?.cardMarketPrice?.avg ?? 0
  const priceWhenAdded = userPrice ?? defaultPrice

  const handleConfirm = () => {
    if (!condition) return
    if (priceError) return
    const price = Number.isNaN(priceWhenAdded) || priceWhenAdded <= 0 ? null : priceWhenAdded
    onConfirm(condition, price)
  }

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (!isOpen) return null

  const apiPrice = card?.cardMarketPrice ?? null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" onClick={handleBackdropClick}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-xl border border-gray-200" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-2xl font-semibold text-gray-900 m-0">Add to Inventory</h3>
          <button onClick={onClose} className="text-gray-600 hover:text-gray-900 transition-colors p-1 rounded-md hover:bg-gray-100" aria-label="Close modal">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        {card && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <p className="text-gray-900 font-semibold text-lg m-0 mb-1">{card.name}</p>
            {card.set && <p className="text-gray-600 text-sm m-0">{card.set.name}</p>}
          </div>
        )}

        <div className="space-y-6">
          <div>
            <label className="block text-gray-700 font-medium mb-2 text-sm">Condition</label>
            <select
              value={condition}
              onChange={(e) => setCondition(e.target.value as StandardCondition)}
              className="w-full py-2 px-3 bg-white border border-gray-300 rounded-lg text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {STANDARD_CONDITIONS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>

          <PriceInput
            value={priceWhenAdded}
            onChange={(v) => setUserPrice(Number.isNaN(v) ? null : v)}
            onError={setPriceError}
            label="Price when added"
            placeholder="0.00"
            showApiPriceHint={true}
            apiPrice={apiPrice?.avg ?? null}
            helperText={!apiPrice ? 'No API price available.' : undefined}
          />
        </div>

        <div className="flex gap-3 mt-8">
          <button onClick={onClose} className="flex-1 py-3 px-6 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg font-semibold hover:bg-gray-200">
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!condition || !!priceError}
            className="flex-1 py-3 px-6 bg-emerald-600 text-white border-none rounded-lg font-semibold hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Add Card
          </button>
        </div>
      </div>
    </div>
  )
}
